import Button from '@material-ui/core/Button';
import Card from '../../../../components/Card/CardTS';
import CardBody from '../../../../components/Card/CardBody.js';
import CardHeader from '../../../../components/Card/CardHeader.js';
import CardIcon from '../../../../components/Card/CardIcon.js';
import React, { useEffect, useRef, useState } from 'react';
import regularFormsStyle from '../../../../assets/jss/material-dashboard-pro-react/views/regularFormsStyleTS';
import { ClassNameMap } from '@material-ui/core/styles/withStyles';
import FormControl from '@material-ui/core/FormControl';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Checkbox from '@material-ui/core/Checkbox';
import TextField from '@material-ui/core/TextField';
import { getSystemsForInstallerPanel, setSystemArchived, closeSetSystemArchivedNotification } from '../../../stores/actions/installerActions';
import { connect } from 'react-redux';
import Person from '@material-ui/icons/Person';
import Autocomplete from '@material-ui/lab/Autocomplete';
import Snackbar from '../../../../components/Snackbar/Snackbar';
import '../InstallerPanel.css';

interface ISystemForInstallerPanel {
    ID: number;
    name: string;
    systemVersion: string;
    archived: boolean;
}

interface IArchiveSystemProps {
    classes: ClassNameMap;
    mobileView: boolean;
    systems: ISystemForInstallerPanel[];
    getSystemsForInstallerPanel: () => void;
    setSystemArchived: (systemID: number, archived: boolean) => void;
    closeSetSystemArchivedNotification: () => void;
    setSystemArchivedSuccessfully: boolean;
    setSystemArchivedFailed: boolean;
}

const ArchiveSystem = ({
    mobileView,
    systems,
    getSystemsForInstallerPanel,
    setSystemArchived,
    closeSetSystemArchivedNotification,
    setSystemArchivedSuccessfully,
    setSystemArchivedFailed,
    ...props
}: IArchiveSystemProps) => {
    const { classes } = props;
    const [showArchivedOnly, setShowArchivedOnly] = useState<boolean>(false);
    const [pickedSystem, setPickedSystem] = useState<number|null|undefined>(null);
    const visibleSystems = systems.filter((s) => s.archived === showArchivedOnly);
    const systemIds = visibleSystems.map((el) => el.ID);
    const systemNamesByIds = Object.fromEntries(visibleSystems.map((x) => [x.ID, x.name]));
    const pickedSystemData = visibleSystems.find((s) => s.ID === pickedSystem);
    const archivedCount = systems.filter((s) => s.archived).length;
    const closeNotificationTimer = useRef(null);

    useEffect(() => {
        getSystemsForInstallerPanel();
    }, [getSystemsForInstallerPanel]);

    useEffect(() => {
        if (setSystemArchivedSuccessfully || setSystemArchivedFailed) {
            clearTimeout(closeNotificationTimer.current);
            closeNotificationTimer.current = setTimeout(closeSetSystemArchivedNotification, 6000);
        }
        return () => {
            clearTimeout(closeNotificationTimer.current);
        };
    }, [setSystemArchivedSuccessfully, setSystemArchivedFailed, closeSetSystemArchivedNotification]);

    useEffect(() => {
        if (setSystemArchivedSuccessfully) {
            setPickedSystem(null);
        }
    }, [setSystemArchivedSuccessfully]);

    const handleShowArchivedOnlyChange = (checked: boolean): void => {
        setShowArchivedOnly(checked);
        setPickedSystem(null);
    };

    const handleToggleArchived = (): void => {
        if (pickedSystemData) {
            setSystemArchived(pickedSystemData.ID, !pickedSystemData.archived);
        }
    };

    const toggleButton = (): JSX.Element => {
        return (
            <Button
                size='medium'
                style={{ marginTop: '25px', transition: 'color 0.3s' }}
                disabled={!pickedSystemData}
                onClick={() => handleToggleArchived()}
            >
                {showArchivedOnly ? 'Unarchive' : 'Archive'}
            </Button>
        );
    };

    if (systems.length < 1) {
        return (
            <Card>
                <CardHeader color='info' icon>
                    <CardIcon color='info'>
                        <Person />
                    </CardIcon>
                    <h4 className={classes.cardIconTitle}>
                        Archive / Unarchive System
                    </h4>
                </CardHeader>
                <CardBody>Please add a system first.</CardBody>
            </Card>
        );
    }

    return (
        <Card className='inviteUserForm'>
            <CardHeader color='info' icon>
                <CardIcon color='info'>
                    <Person />
                </CardIcon>
                <h4 className={classes.cardIconTitle}>Archive / Unarchive System</h4>
            </CardHeader>
            <CardBody>
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={showArchivedOnly}
                            onChange={(e) => handleShowArchivedOnlyChange(e.target.checked)}
                            color='primary'
                        />
                    }
                    label={`Show archived systems (${archivedCount})`}
                />
                <FormControl fullWidth className={classes.selectFormControl} onSubmit={(e) => e.preventDefault()}>
                    <div style={{ width: '100%' }}>
                        <Autocomplete
                            options={systemIds}
                            getOptionLabel={(id) => systemNamesByIds[id]}
                            id='system-name'
                            value={pickedSystem}
                            onChange={(e, newVal) => setPickedSystem(newVal)}
                            renderInput={(systemIds) => (
                                <TextField {...systemIds} label={showArchivedOnly ? 'Archived systems' : 'Active systems'} margin='normal' />
                            )}
                            style={{ width: '100%' }}
                            noOptionsText={showArchivedOnly ? 'No archived systems' : 'No active systems'}
                        />
                    </div>
                </FormControl>
                {toggleButton()}
            </CardBody>
            <Snackbar
                close
                closeNotification={() => closeSetSystemArchivedNotification()}
                color={setSystemArchivedFailed ? 'danger' : 'success'}
                message={
                    setSystemArchivedFailed
                        ? 'The system archived state could not be updated.'
                        : `System ${showArchivedOnly ? 'unarchived' : 'archived'} successfully.`
                }
                open={setSystemArchivedFailed || setSystemArchivedSuccessfully}
                place='tc'
            />
        </Card>
    );
};

const mapStateToProps = (state) => ({
    systems: state.installer.systemsForInstallerPanel || [],
    mobileView: state.dashboard.mobileView,
    setSystemArchivedSuccessfully: state.installer.setSystemArchivedSuccessfully,
    setSystemArchivedFailed: state.installer.setSystemArchivedFailed,
});
const actions = {
    getSystemsForInstallerPanel,
    setSystemArchived,
    closeSetSystemArchivedNotification,
};

const ArchiveSystemWithStyles = regularFormsStyle(ArchiveSystem);
export default connect(mapStateToProps, actions)(ArchiveSystemWithStyles);
